import { Box, Stack, Typography, useTheme } from "@mui/material";
import { FC } from "react";
import { RemovableChip } from "../../../components";
import { useLocalization } from "../../../hooks/useLocalization";

interface AddedEmailsListProps {
  addedEmailAddresses: string[];
  onRemoveEmail: (email: string) => void;
  duplicatesCount?: number;
  invalidEmailsCount?: number;
  alreadyWhitelistedCount?: number;
  fileName?: string;
}

export const AddedEmailsList: FC<AddedEmailsListProps> = ({
  addedEmailAddresses,
  onRemoveEmail,
  duplicatesCount = 0,
  invalidEmailsCount = 0,
  alreadyWhitelistedCount = 0,
  fileName,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const hasIssues =
    duplicatesCount > 0 ||
    invalidEmailsCount > 0 ||
    alreadyWhitelistedCount > 0;

  const getIssuesText = () => {
    const issues = [];

    if (duplicatesCount > 0) {
      issues.push(
        t("licenses.addToWhitelistDialog.duplicateEmail", {
          count: duplicatesCount,
        }),
      );
    }

    if (alreadyWhitelistedCount > 0) {
      issues.push(
        t("licenses.addToWhitelistDialog.alreadyWhitelisted", {
          count: alreadyWhitelistedCount,
        }),
      );
    }

    if (invalidEmailsCount > 0) {
      issues.push(
        t("licenses.addToWhitelistDialog.invalidEmailCount", {
          count: invalidEmailsCount,
        }),
      );
    }

    const issuesText = issues.join(", ");
    const totalCount =
      duplicatesCount + alreadyWhitelistedCount + invalidEmailsCount;

    if (fileName) {
      return t("licenses.addToWhitelistDialog.issuesFoundInFile", {
        count: totalCount,
        issues: issuesText,
        fileName,
      });
    }

    return t("licenses.addToWhitelistDialog.issuesText", {
      count: totalCount,
      issues: issuesText,
    });
  };

  return (
    <Stack sx={{ height: { xs: `calc(100vh - 400px)`, md: 400 } }}>
      <Typography
        fontSize={12}
        sx={{ color: theme.colors.text[1], fontWeight: 400, mb: 1 }}
      >
        {t("licenses.addToWhitelistDialog.addedEmailsTitle")}{" "}
        {addedEmailAddresses.length > 0 && `(${addedEmailAddresses.length})`}
      </Typography>
      {hasIssues && (
        <Typography
          fontSize={12}
          sx={{ color: theme.palette.warning.main, fontWeight: 500, mb: 1 }}
        >
          {getIssuesText()}
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          overflowY: "auto",
        }}
      >
        {addedEmailAddresses.length > 0 ? (
          addedEmailAddresses.map((email) => (
            <RemovableChip
              key={email}
              label={email}
              onDelete={() => onRemoveEmail(email)}
            />
          ))
        ) : (
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: theme.colors.text[1],
              width: "100%",
              textAlign: "center",
              paddingTop: 4,
            }}
          >
            {t("licenses.addToWhitelistDialog.noEmailsAdded")}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
